"use client"

import { useState, useEffect, useRef } from "react"
import "./ScopingProcess.css"

const ScopingProcess = () => {
  const [activeTab, setActiveTab] = useState("planning")
  const headerRef = useRef(null)
  const textRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    analyze: {
      title: "ANALYZE",
      items: [
        "Comprehensive analysis of your current systems and processes",
        "Identification of pain points and bottlenecks",
        "Assessment of technical requirements and constraints",
        "Stakeholder interviews and requirement gathering",
      ],
    },
    discovery: {
      title: "DISCOVERY",
      items: [
        "Market research and competitive analysis",
        "User journey mapping and persona development",
        "Technology stack evaluation and recommendations",
        "Feasibility assessment of proposed solutions",
      ],
    },
    planning: {
      title: "PLANNING",
      subtitle1: "1. HIGH-LEVEL ARCHITECTURE",
      subtitle2: "2. PROJECT PLAN",
      description:
        "Create a detailed roadmap that will be used to identify the main features the product will have and prioritize them.",
      items: [
        "Tasks",
        "Timeline",
        "Development of the technical design, with the best solutions selected, accessibility and reach",
        "Identification of external dependencies from third-party providers (Stripe, Facebook, Google, etc)",
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = [headerRef.current, textRef.current, imageRef.current, tabsRef.current]
    elements.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      elements.forEach((el) => {
        if (el) observer.unobserve(el)
      })
    }
  }, [])

  return (
    <div className="scoping-container" id ="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Header */}
        <div className="header-section" ref={headerRef}>
          <h2 className="header-title">
            OUR
            <br />
            <span className="highlight">SCOPING SESSION</span>
            <br />
            <span className="highlight">PROCESS</span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="text-section" ref={textRef}>
            <p className="main-description">
              The scoping session starts from the planning stage from a Project Manager and the interaction from
              developers and designers to build a solid roadmap to give shape for your project and estimate it.
            </p>
          </div>
          <div className="image-section" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "analyze" ? "active" : ""}`}
              onClick={() => setActiveTab("analyze")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 20h20M5 20V8.5M9 20v-9M13 20v-5.5M17 20v-9"></path>
                </svg>
              </div>
              ANALYZE
            </button>
            <button
              className={`tab-button ${activeTab === "discovery" ? "active" : ""}`}
              onClick={() => setActiveTab("discovery")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </div>
              DISCOVERY
            </button>
            <button
              className={`tab-button ${activeTab === "planning" ? "active" : ""}`}
              onClick={() => setActiveTab("planning")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              PLANNING
            </button>
          </div>

          <div className="tab-content">
            <div className={`tab-panel ${activeTab === "analyze" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.analyze.title}</h3>
              <ul className="tab-list">
                {tabContent.analyze.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "discovery" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.discovery.title}</h3>
              <ul className="tab-list">
                {tabContent.discovery.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "planning" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.planning.title}</h3>
              <h4 className="tab-subtitle">{tabContent.planning.subtitle1}</h4>
              <h4 className="tab-subtitle">{tabContent.planning.subtitle2}</h4>
              <p className="tab-description">{tabContent.planning.description}</p>
              <ul className="tab-list">
                {tabContent.planning.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ScopingProcess;
